import React from "react"
import { useTranslation } from "react-i18next"

import { useActiveOrganization, useAuthorization } from "@/contexts"
import type { Organization } from "@/models/organization"
import { HighlightedSearchText } from "@/nzds/utils"
import { singleLineEllipsisStyles } from "@/styles/utils"
import { useSwitchOrganization } from "@/utils/url"

import CloseIcon from "@mui/icons-material/Close"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Typography,
  useTheme,
} from "@mui/material"

const ORG_SWITCHER_LIST_ID = "org-switcher-popover-list"
const SEARCH_THRESHOLD = 5

const createOptions = (organizations: Organization[]) =>
  organizations
    .map((organization) => ({
      id: organization.id ?? "",
      label: organization.name,
    }))
    .filter((organization) => organization.id !== "")

export const OrganizationNameSwitcher: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { authorizedOrganizations, authorizedOrganizationsIsFetched } =
    useAuthorization()
  const { activeOrganization } = useActiveOrganization()
  const { switchToOrganization } = useSwitchOrganization()

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const [searchQuery, setSearchQuery] = React.useState("")
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)
  const isOpen = Boolean(anchorEl)
  const organizationOptions = createOptions(authorizedOrganizations)
  const isMultiOrg =
    authorizedOrganizationsIsFetched && organizationOptions.length > 1
  const showSearch = organizationOptions.length > SEARCH_THRESHOLD
  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const filteredOptions =
    !showSearch || !normalizedSearchQuery
      ? organizationOptions
      : organizationOptions.filter((option) =>
          option.label.toLowerCase().includes(normalizedSearchQuery)
        )

  const triggerAriaLabel = activeOrganization?.name
    ? t("organizationSwitcher.triggerAriaLabel", {
        ns: "common",
        orgName: activeOrganization.name,
      })
    : undefined

  const organizationNameColorStyle = {
    color: theme.palette.text.secondary,
  }

  const closePopover = () => {
    setAnchorEl(null)
    setSearchQuery("")
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleSelectOrganization = (organizationId: string) => {
    switchToOrganization(organizationId)
    closePopover()
  }

  const focusFirstListItem = () => {
    const firstItem =
      listRef.current?.querySelector<HTMLElement>('[role="menuitem"]')
    firstItem?.focus()
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      focusFirstListItem()
    }
  }

  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    const focusTimer = globalThis.setTimeout(() => {
      if (showSearch) {
        searchInputRef.current?.focus()
        return
      }

      focusFirstListItem()
    }, 0)

    return () => {
      globalThis.clearTimeout(focusTimer)
    }
  }, [isOpen, showSearch])

  if (!activeOrganization?.name) {
    return null
  }

  if (!isMultiOrg) {
    return (
      <Typography
        component="p"
        sx={{
          ...organizationNameColorStyle,
          ...singleLineEllipsisStyles,
          alignSelf: "flex-start",
          maxWidth: "100%",
          mb: -0.5,
          ml: 0.25,
          width: "fit-content",
        }}
        variant="h3"
      >
        {activeOrganization.name}
      </Typography>
    )
  }

  return (
    <>
      <Button
        aria-controls={isOpen ? ORG_SWITCHER_LIST_ID : undefined}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={triggerAriaLabel}
        color="inherit"
        onClick={handleOpen}
        sx={{
          ...organizationNameColorStyle,
          alignItems: "center",
          alignSelf: "flex-start",
          display: "inline-flex",
          gap: 0.25,
          justifyContent: "flex-start",
          left: -6,
          maxWidth: "100%",
          mb: -0.5,
          minHeight: 0,
          minWidth: "unset",
          position: "relative",
          px: 1,
          py: 0,
          textTransform: "none",
          typography: "h3",
          width: "fit-content",
          "&:hover": {
            backgroundColor: "action.hover",
          },
          "&:focus-visible": {
            outline: `1px solid ${theme.palette.primary.main}`,
          },
        }}
        variant="text"
      >
        <Box component="span" sx={{ ...singleLineEllipsisStyles, minWidth: 0 }}>
          {activeOrganization.name}
        </Box>
        <KeyboardArrowDownIcon
          fontSize="inherit"
          sx={{
            flexShrink: 0,
            transform: isOpen ? "rotate(180deg)" : "none",
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.shortest,
            }),
          }}
        />
      </Button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
        onClose={closePopover}
        open={isOpen}
        slotProps={{
          paper: {
            sx: {
              maxHeight: "calc(100vh - 120px)",
              minWidth: 280,
              mt: 0.5,
            },
          },
        }}
        transformOrigin={{
          horizontal: "left",
          vertical: "top",
        }}
      >
        {showSearch ? (
          <Box sx={{ p: 1.5, pb: 0 }}>
            <TextField
              fullWidth
              inputRef={searchInputRef}
              onChange={(event) => {
                setSearchQuery(event.target.value)
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder={t("organizationSearch.label", { ns: "common" })}
              size="small"
              slotProps={{
                htmlInput: {
                  "aria-label": t("organizationSearch.label", { ns: "common" }),
                },
                input: {
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t("organizationSearch.clear", {
                          ns: "common",
                        })}
                        edge="end"
                        onClick={() => {
                          setSearchQuery("")
                          searchInputRef.current?.focus()
                        }}
                        onMouseDown={(event) => {
                          event.preventDefault()
                        }}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              value={searchQuery}
              variant="outlined"
            />
          </Box>
        ) : null}
        <MenuList
          autoFocusItem={!showSearch}
          id={ORG_SWITCHER_LIST_ID}
          ref={listRef}
          sx={{
            maxHeight: showSearch ? "calc(100vh - 200px)" : undefined,
            overflowY: "auto",
            py: 0.5,
          }}
        >
          {filteredOptions.length === 0 ? (
            <MenuItem disabled>
              {t("organizationSearch.notFound", { ns: "common" })}
            </MenuItem>
          ) : (
            filteredOptions.map((option) => (
              <MenuItem
                key={option.id}
                onClick={() => {
                  handleSelectOrganization(option.id)
                }}
                selected={option.id === activeOrganization.id}
                sx={{ whiteSpace: "normal" }}
              >
                {showSearch && normalizedSearchQuery ? (
                  <HighlightedSearchText
                    searchText={searchQuery}
                    text={option.label}
                  />
                ) : (
                  option.label
                )}
              </MenuItem>
            ))
          )}
        </MenuList>
      </Popover>
    </>
  )
}
